import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Suspense, useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { DonutShare } from '../../components/charts';
import { ExportMenu } from '../../components/ExportMenu';
import { KpiCard } from '../../components/KpiCard';
import { Modal } from '../../components/Modal';
import { PageHeader } from '../../components/PageHeader';
import { Pill, type PillTone } from '../../components/Pill';
import { DataTable } from '../../components/Table';
import type { InventoryRow } from '../../data/fetchers';
import {
  getInventoryReport,
  listBranches,
  listCategories,
  listMovements,
  listProducts,
} from '../../data/fetchers';
import { CHART_H } from '../../lib/chartTokens';
import type { ExportCol } from '../../lib/export';
import {
  fmtAmount,
  fmtBahtShort,
  fmtInt,
  fmtStockBreakdown,
} from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';

const TYPE_TH: Record<string, string> = {
  sale: 'ขายสินค้า',
  receive: 'รับสินค้าเข้า',
  adjust: 'ปรับปรุงสต๊อก',
  'transfer-in': 'รับโอนย้าย',
  'transfer-out': 'ส่งโอนย้าย',
};

const TYPE_TONE: Record<string, PillTone> = {
  sale: 'primary',
  receive: 'ok',
  adjust: 'warn',
  'transfer-in': 'ok',
  'transfer-out': 'danger',
};

interface InventoryExportRow {
  branchShort?: string;
  productId: string;
  name: string;
  category: string;
  onHandDisplay: string;
  onHandBaseUnits: number;
  valueBaht: string;
  valueSatang: number;
  status: string;
}

export function InventoryReportView() {
  const { scope, ready } = useBranchScope();

  // Filters State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBranch, setSelectedBranch] = useState('all');

  // Drill-down Modal State
  const [drillDownRow, setDrillDownRow] = useState<InventoryRow | null>(null);

  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: listProducts,
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['report', 'inventory', scope.branchId, scope.allowedBranchIds],
    queryFn: () => getInventoryReport(scope),
    enabled: ready,
  });

  const showBranchCol = scope.branchId === null;

  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
    enabled: ready && showBranchCol,
  });

  const { data: movements = [] } = useQuery({
    queryKey: ['movements', scope.branchId, scope.allowedBranchIds],
    queryFn: () => listMovements(scope),
    enabled: ready && !!drillDownRow,
  });

  const rows = useMemo<InventoryRow[]>(() => data?.rows ?? [], [data]);

  // Filter logic
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      // 1. Search Query (SKU or Name)
      const q = searchQuery.trim().toLowerCase();
      if (
        q &&
        !r.name.toLowerCase().includes(q) &&
        !r.productId.toLowerCase().includes(q)
      ) {
        return false;
      }
      // 2. Category Filter
      if (selectedCategory !== 'all' && r.categoryId !== selectedCategory) {
        return false;
      }
      // 3. Branch Filter (if applicable)
      if (
        showBranchCol &&
        selectedBranch !== 'all' &&
        r.branchId !== selectedBranch
      ) {
        return false;
      }
      // 4. Stock Status Filter
      if (selectedStatus !== 'all') {
        const product = products.find((p) => p.id === r.productId);
        const minStock = product?.minStock ?? 0;
        const isOutOfStock = r.onHand <= 0;
        const isLowStock = r.onHand > 0 && r.onHand < minStock;

        if (selectedStatus === 'outOfStock' && !isOutOfStock) return false;
        if (selectedStatus === 'lowStock' && !isLowStock) return false;
        if (selectedStatus === 'normal' && (isOutOfStock || isLowStock))
          return false;
      }
      return true;
    });
  }, [
    rows,
    searchQuery,
    selectedCategory,
    selectedStatus,
    selectedBranch,
    showBranchCol,
    products,
  ]);

  // Metrics derived from filtered list
  const metrics = useMemo(() => {
    const totalVal = filteredRows.reduce((s, r) => s + r.value, 0);
    const totalUnits = filteredRows.reduce((s, r) => s + r.onHand, 0);
    const lowCount = filteredRows.filter((r) => {
      const product = products.find((p) => p.id === r.productId);
      const minStock = product?.minStock ?? 0;
      return r.onHand > 0 && r.onHand < minStock;
    }).length;
    const outOfStockCount = filteredRows.filter((r) => r.onHand <= 0).length;

    return { totalVal, totalUnits, lowCount, outOfStockCount };
  }, [filteredRows, products]);

  // Donut 1: Value by Category
  const categoryDonutData = useMemo(() => {
    const byCategory = new Map<string, number>();
    for (const row of filteredRows) {
      if (row.value > 0) {
        byCategory.set(
          row.categoryId,
          (byCategory.get(row.categoryId) ?? 0) + row.value,
        );
      }
    }
    return [...byCategory.entries()].map(([categoryId, value]) => {
      const cat = categories.find((c) => c.id === categoryId);
      return { label: cat?.name ?? categoryId, value };
    });
  }, [filteredRows, categories]);

  // Donut 2: Value by Branch (if owner) OR Top 5 Products by Value (if single branch)
  const secondaryDonutData = useMemo(() => {
    if (showBranchCol) {
      const byBranch = new Map<string, number>();
      for (const row of filteredRows) {
        if (row.value > 0) {
          byBranch.set(
            row.branchShort,
            (byBranch.get(row.branchShort) ?? 0) + row.value,
          );
        }
      }
      return [...byBranch.entries()].map(([branchShort, value]) => ({
        label: branchShort,
        value,
      }));
    } else {
      const sorted = [...filteredRows].sort((a, b) => b.value - a.value);
      const top5 = sorted.slice(0, 5);
      const otherValue = sorted.slice(5).reduce((s, r) => s + r.value, 0);
      const data = top5.map((r) => ({ label: r.name, value: r.value }));
      if (otherValue > 0) {
        data.push({ label: 'อื่นๆ', value: otherValue });
      }
      return data;
    }
  }, [filteredRows, showBranchCol]);

  // Columns definition
  const columns = useMemo<ColumnDef<InventoryRow, unknown>[]>(() => {
    const cols: ColumnDef<InventoryRow, unknown>[] = [];

    if (showBranchCol) {
      cols.push({
        accessorKey: 'branchShort',
        header: 'สาขา',
        cell: (c) => (
          <span className={c.row.original.onHand <= 0 ? 'text-ink-4' : ''}>
            {c.getValue<string>()}
          </span>
        ),
      });
    }

    cols.push({
      accessorKey: 'productId',
      header: 'รหัสสินค้า',
      cell: (c) => (
        <span
          className={`num ${c.row.original.onHand <= 0 ? 'text-ink-4' : ''}`}
        >
          {c.getValue<string>()}
        </span>
      ),
    });

    cols.push({
      accessorKey: 'name',
      header: 'สินค้า',
      cell: (c) => (
        <span
          className={
            c.row.original.onHand <= 0
              ? 'text-ink-4 line-through'
              : 'font-semibold'
          }
        >
          {c.getValue<string>()}
        </span>
      ),
    });

    cols.push({
      accessorKey: 'categoryId',
      header: 'หมวดหมู่',
      cell: (c) => {
        const catId = c.getValue<string>();
        const catName = categories.find((x) => x.id === catId)?.name ?? catId;
        return (
          <span className={c.row.original.onHand <= 0 ? 'text-ink-4' : ''}>
            {catName}
          </span>
        );
      },
    });

    cols.push({
      accessorKey: 'onHand',
      header: 'คงเหลือ',
      cell: (c) => {
        const row = c.row.original;
        const product = products.find((p) => p.id === row.productId);
        const breakdown = product
          ? fmtStockBreakdown(row.onHand, product)
          : fmtInt(row.onHand);
        return (
          <span
            className={`num font-bold ${row.onHand <= 0 ? 'text-danger' : ''}`}
          >
            {breakdown}
          </span>
        );
      },
    });

    cols.push({
      accessorKey: 'value',
      header: 'มูลค่าสต๊อก (ทุน)',
      cell: (c) => (
        <span
          className={`num ${c.row.original.onHand <= 0 ? 'text-ink-4' : ''}`}
        >
          {fmtAmount(c.getValue<number>())}
        </span>
      ),
    });

    cols.push({
      id: 'status',
      header: 'สถานะ',
      cell: (c) => {
        const row = c.row.original;
        const product = products.find((p) => p.id === row.productId);
        const minStock = product?.minStock ?? 0;

        if (row.onHand <= 0) {
          return <Pill tone="danger">สินค้าหมด</Pill>;
        }
        if (row.onHand < minStock) {
          return <Pill tone="warn">สต๊อกต่ำ ({minStock})</Pill>;
        }
        return <Pill tone="ok">ปกติ</Pill>;
      },
    });

    cols.push({
      id: 'actions',
      header: 'การจัดการ',
      cell: (c) => {
        const row = c.row.original;
        return (
          <Btn kind="ghost" size="sm" onClick={() => setDrillDownRow(row)}>
            ประวัติสต๊อก
          </Btn>
        );
      },
    });

    return cols;
  }, [showBranchCol, products, categories]);

  const exportRows = useMemo<InventoryExportRow[]>(
    () =>
      filteredRows.map((row) => {
        const product = products.find((p) => p.id === row.productId);
        const minStock = product?.minStock ?? 0;
        const status =
          row.onHand <= 0
            ? 'สินค้าหมด'
            : row.onHand < minStock
              ? `สต๊อกต่ำ (${minStock})`
              : 'ปกติ';
        return {
          branchShort: row.branchShort,
          productId: row.productId,
          name: row.name,
          category:
            categories.find((category) => category.id === row.categoryId)
              ?.name ?? row.categoryId,
          onHandDisplay: product
            ? fmtStockBreakdown(row.onHand, product)
            : fmtInt(row.onHand),
          onHandBaseUnits: row.onHand,
          valueBaht: fmtAmount(row.value),
          valueSatang: row.value,
          status,
        };
      }),
    [filteredRows, products, categories],
  );

  const exportCols = useMemo<ExportCol<InventoryExportRow>[]>(() => {
    const base: ExportCol<InventoryExportRow>[] = [
      { key: 'productId', header: 'รหัสสินค้า' },
      { key: 'name', header: 'สินค้า' },
      { key: 'category', header: 'หมวดหมู่' },
      { key: 'onHandDisplay', header: 'คงเหลือ' },
      { key: 'onHandBaseUnits', header: 'คงเหลือ (base units)' },
      { key: 'valueBaht', header: 'มูลค่าสต๊อก (บาท)' },
      { key: 'valueSatang', header: 'มูลค่าสต๊อก (satang)' },
      { key: 'status', header: 'สถานะ' },
    ];
    if (showBranchCol) return [{ key: 'branchShort', header: 'สาขา' }, ...base];
    return base;
  }, [showBranchCol]);

  // Drill-down data
  const drillDownProduct = useMemo(() => {
    if (!drillDownRow) return null;
    return products.find((p) => p.id === drillDownRow.productId) ?? null;
  }, [products, drillDownRow]);

  const drillDownProductMovements = useMemo(() => {
    if (!drillDownRow) return [];
    return movements
      .filter(
        (m) =>
          m.productId === drillDownRow.productId &&
          m.branchId === drillDownRow.branchId,
      )
      .sort((a, b) => b.at - a.at);
  }, [movements, drillDownRow]);

  return (
    <div className="bo-fade space-y-6">
      <PageHeader
        title="รายงานสต๊อก"
        subtitle="ประเมินมูลค่าตามต้นทุน (ต้นทุนเฉลี่ยเคลื่อนที่ × คงเหลือ) — ไม่ใช่ราคาขาย"
        actions={
          <ExportMenu
            rows={exportRows}
            cols={exportCols}
            filename="รายงานสต๊อก"
            title="รายงานสต๊อก"
            pdf
          />
        }
      />

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="มูลค่าสต๊อกรวม (ตามต้นทุน)"
          value={ready && data ? fmtAmount(metrics.totalVal) : '—'}
          unit="บาท"
          tone="primary"
        />
        <KpiCard
          label="จำนวนสินค้าในคลัง"
          value={ready && data ? fmtInt(metrics.totalUnits) : '—'}
          unit="ชิ้น"
          tone="accent"
        />
        <KpiCard
          label="รายการสต๊อกต่ำ"
          value={ready && data ? fmtInt(metrics.lowCount) : '—'}
          unit="รายการ"
          tone="warn"
        />
        <KpiCard
          label="รายการสต๊อกหมด"
          value={ready && data ? fmtInt(metrics.outOfStockCount) : '—'}
          unit="รายการ"
          tone="danger"
        />
      </div>

      {/* Interactive Filters Panel */}
      <div className="flex flex-wrap items-end justify-between gap-4 p-4 bo-card bg-surface-2/30">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-ink-3">
              ค้นหาสินค้า
            </span>
            <input
              type="text"
              placeholder="รหัสสินค้า หรือ ชื่อสินค้า..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-3 py-1.5 text-sm rounded-btn border border-line bg-surface text-ink placeholder:text-ink-3 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 w-48"
            />
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-ink-3">หมวดหมู่</span>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-1.5 text-sm rounded-btn border border-line bg-surface text-ink focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
            >
              <option value="all">หมวดหมู่ทั้งหมด</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {showBranchCol && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-ink-3">สาขา</span>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="px-3 py-1.5 text-sm rounded-btn border border-line bg-surface text-ink focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
              >
                <option value="all">ทุกสาขา</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-ink-3">สถานะสต๊อก</span>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-1.5 text-sm rounded-btn border border-line bg-surface text-ink focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
            >
              <option value="all">สถานะทั้งหมด</option>
              <option value="normal">ปกติ</option>
              <option value="lowStock">สต๊อกต่ำกว่าเกณฑ์</option>
              <option value="outOfStock">สินค้าหมดคลัง</option>
            </select>
          </div>
        </div>

        {(searchQuery ||
          selectedCategory !== 'all' ||
          selectedBranch !== 'all' ||
          selectedStatus !== 'all') && (
          <Btn
            kind="ghost"
            size="sm"
            onClick={() => {
              setSearchQuery('');
              setSelectedCategory('all');
              setSelectedBranch('all');
              setSelectedStatus('all');
            }}
          >
            ล้างตัวกรองทั้งหมด
          </Btn>
        )}
      </div>

      {/* Double Charts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card title="สัดส่วนมูลค่าสต๊อกตามหมวดหมู่">
          <Suspense
            fallback={
              <div
                className="bo-card animate-pulse"
                style={{ height: CHART_H.md }}
              />
            }
          >
            <DonutShare data={categoryDonutData} valueFormat={fmtBahtShort} />
          </Suspense>
        </Card>

        <Card
          title={
            showBranchCol
              ? 'สัดส่วนมูลค่าสต๊อกตามสาขา'
              : 'สัดส่วนมูลค่าสต๊อกของสินค้า Top 5'
          }
        >
          <Suspense
            fallback={
              <div
                className="bo-card animate-pulse"
                style={{ height: CHART_H.md }}
              />
            }
          >
            <DonutShare data={secondaryDonutData} valueFormat={fmtBahtShort} />
          </Suspense>
        </Card>
      </div>

      {/* Main Stock Table */}
      {!ready || isLoading ? (
        <div className="bo-card h-40 animate-pulse bg-bg-warm" />
      ) : (
        <DataTable
          columns={columns}
          data={filteredRows}
          emptyMessage="ไม่พบข้อมูลสินค้าตรงกับตัวกรองที่เลือก"
        />
      )}

      {/* Stock History Drill-down Modal */}
      {drillDownRow && (
        <Modal
          open={!!drillDownRow}
          title={
            <span className="flex items-center gap-2">
              <span>ประวัติการเคลื่อนไหวสต๊อก</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary-light text-primary">
                {drillDownRow.productId}
              </span>
            </span>
          }
          onClose={() => setDrillDownRow(null)}
          className="w-[min(650px,94vw)]"
        >
          <div className="space-y-4">
            <div>
              <div className="text-body font-bold text-ink">
                {drillDownRow.name}
              </div>
              <div className="text-xs text-ink-3 mt-0.5">
                สาขา: {drillDownRow.branchShort}
              </div>
            </div>

            {/* Drilldown Summary Cards */}
            <div className="grid grid-cols-3 gap-3 bg-bg-warm/40 p-3 rounded-card border border-line-2">
              <div className="text-center">
                <div className="text-[10px] font-bold text-ink-3 uppercase">
                  คงเหลือปัจจุบัน
                </div>
                <div className="text-sm font-extrabold text-ink mt-1">
                  {drillDownProduct
                    ? fmtStockBreakdown(drillDownRow.onHand, drillDownProduct)
                    : drillDownRow.onHand}
                </div>
              </div>
              <div className="text-center border-x border-line-2">
                <div className="text-[10px] font-bold text-ink-3 uppercase">
                  มูลค่าคงเหลือ (ทุน)
                </div>
                <div className="text-sm font-extrabold text-ink mt-1">
                  {fmtAmount(drillDownRow.value)} ฿
                </div>
              </div>
              <div className="text-center">
                <div className="text-[10px] font-bold text-ink-3 uppercase">
                  การเคลื่อนไหว
                </div>
                <div className="text-sm font-extrabold text-ink mt-1">
                  {drillDownProductMovements.length} ครั้ง
                </div>
              </div>
            </div>

            {/* Movements List Table */}
            <div className="overflow-y-auto max-h-[320px] rounded-card border border-line-2">
              <table className="bo-table text-xs">
                <thead className="sticky top-0 bg-surface z-10">
                  <tr>
                    <th>วัน-เวลา</th>
                    <th>ประเภท</th>
                    <th className="text-right">จำนวนที่เปลี่ยน</th>
                    <th>เหตุผล / อ้างอิง</th>
                  </tr>
                </thead>
                <tbody>
                  {drillDownProductMovements.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-ink-3 py-10">
                        ไม่มีประวัติการเคลื่อนไหวในสาขานี้
                      </td>
                    </tr>
                  ) : (
                    drillDownProductMovements.map((m) => (
                      <tr key={m.id} className="hover:bg-bg-warm/20">
                        <td className="num text-ink-2">
                          {new Date(m.at).toLocaleString('th-TH', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </td>
                        <td>
                          <Pill tone={TYPE_TONE[m.type] ?? 'neutral'}>
                            {TYPE_TH[m.type] ?? m.type}
                          </Pill>
                        </td>
                        <td
                          className={`num text-right font-bold ${m.qty < 0 ? 'text-danger' : 'text-ok'}`}
                        >
                          {m.qty > 0 ? '+' : ''}
                          {m.qty.toLocaleString()}
                        </td>
                        <td
                          className="text-ink-2 max-w-[150px] truncate"
                          title={m.reason ?? ''}
                        >
                          {m.reason || m.note || '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
